import { useState, useMemo, useCallback, useEffect } from 'react';
import { NPSDSearchFilters } from '@/components/npsd/NPSDSearchFilters';
import { NPSDSearchResults } from '@/components/npsd/NPSDSearchResults';
import { Dropdownlist } from '@/components/Dropdownlist';
import { useNPSDSearch, NPSDSearchFilter } from '@/hooks/useNPSDSearch';
import { useSearchableFormFields } from '@/hooks/useSearchableFormFields';
import { TextFilter } from '@/components/forms/filters/TextFilter';
import { DateFilter } from '@/components/forms/filters/DateFilter';
import { NumberFilter } from '@/components/forms/filters/NumberFilter';
import { RadioFilter } from '@/components/forms/filters/RadioFilter';
import { SelectFilter } from '@/components/forms/filters/SelectFilter';
import { api } from '@/services/api-class';
import type {
  FormFieldData,
  FormSearchableFieldData,
  FormSearchPredicateData,
  FormSearchResultPageData,
} from '@/services/api-types';

// Custom Form filters (plan §5.1) — MVP "either/or" reduction.
// Currently wired:
//   - Per-schema collapsible sections render the searchable fields of each
//     CLIENT-attached schema's pinned version.
//   - If any schema has predicates set, search switches to form-search mode
//     (api.searchFormInstances) and adapts result.host into client-row shape.
//   - Native filters (name/address/state/website) drive search otherwise.
// MVP deliberately skips (see docs/house-check-request/npsd-search-integration-notes.md):
//   - Native + form intersection (§5.2 "merge with dedup by client id").
//   - Multi-schema AND across schemas (§5.1).
//   - Pagination in form-search mode (page 0 / size 50 only).
//   - Schema-match badge + per-mode column variation.
// The full §5.2 merge is the planned follow-up; nothing here is throwaway.

import { Codes } from '@/constants/codes';
import { getModuleType } from '@/utils/legacy-utils';

interface NPSDSearchClientProps {
  searchMode?: string;
  isMobile?: boolean;
  theme?: string;
  initialValues?: Record<string, any>;
  showNewButton?: boolean;
  onView?: (item: any) => void;
  onAdd?: (item: any) => void;
  onNew?: () => void;
}

export function NPSDSearchClient({
  searchMode = 'default',
  isMobile = false,
  theme = '',
  initialValues = {},
  showNewButton = false,
  onView,
  onAdd,
  onNew,
}: NPSDSearchClientProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState<string | null>('');
  const [website, setWebsite] = useState('');

  const [states, setStates] = useState<any[]>([]);

  const {
    results, totalRecords, currentPage, pageSize, isLoading, hasSearched,
    showViewButton, showAddButton,
    performSearch, clearResults, handlePageChange, handlePageSizeChange,
  } = useNPSDSearch({ entityApiName: 'client', searchMode });

  // Form-filter state (MVP: one schema active at a time; preserve other schemas' state).
  // `formSchemas` here are the searchable-fields projections — one entry per active
  // CLIENT-attached schema with at least one searchable field. The lean shape
  // (key/label/type/options only) is exactly what the filter components need; we
  // never fetch the full schema definition for this view.
  const { schemas: formSchemas } = useSearchableFormFields({ attachTo: 'CLIENT', autoLoad: true });
  const [formPredicatesBySchema, setFormPredicatesBySchema] = useState<Record<number, FormSearchPredicateData[]>>({});
  // Track which schema sections the user has collapsed (inverted from the
  // obvious model — "expanded set" — so that newly loaded schemas default
  // to open without an effect to seed the set).
  const [collapsedSchemaIds, setCollapsedSchemaIds] = useState<Set<number>>(new Set());

  // Form-mode result state (parallel to useNPSDSearch's native state).
  const [formResults, setFormResults] = useState<any[]>([]);
  const [formTotalRecords, setFormTotalRecords] = useState(0);
  const [isFormSearching, setIsFormSearching] = useState(false);
  const [hasFormSearched, setHasFormSearched] = useState(false);
  const [isFormMode, setIsFormMode] = useState(false);

  const columns = useMemo(() => [
    { key: 'name', label: 'Client Name', field: 'name' },
    { key: 'locFormattedAddress', label: 'Address', field: 'locFormattedAddress', fullWidth: true },
    { key: 'website', label: 'Website', field: 'website' },
  ], []);

  // Only show New button in admin module
  const canShowNewButton = useMemo(() => {
    return showNewButton && getModuleType() === 'admin';
  }, [showNewButton]);

  const stateItems = useMemo(() => {
    return states.map((s: any) => ({ label: s.code, value: s.code }));
  }, [states]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (name) count++;
    if (address) count++;
    if (state) count++;
    if (website) count++;
    for (const preds of Object.values(formPredicatesBySchema)) {
      count += preds.length;
    }
    return count;
  }, [name, address, state, website, formPredicatesBySchema]);

  // Load states on mount
  useEffect(() => {
    if (typeof Codes !== 'undefined' && typeof Codes.getStates === 'function') {
      setStates(Codes.getStates() || []);
    }
  }, []);

  // Apply initial values
  useEffect(() => {
    if (initialValues) {
      if (initialValues.name) setName(initialValues.name);
      if (initialValues.address) setAddress(initialValues.address);
      if (initialValues.state) setState(initialValues.state);
      if (initialValues.website) setWebsite(initialValues.website);
    }
  }, [initialValues]);

  const buildFilters = useCallback((): NPSDSearchFilter[] => {
    const filters: NPSDSearchFilter[] = [];

    if (name) {
      filters.push({ field: 'name', operator: 'contains', value: name.trim() });
    }
    if (address) {
      filters.push({ field: 'locFormattedAddress', operator: 'contains', value: address.trim() });
    }
    if (state) {
      filters.push({ field: 'locState', operator: 'contains', value: state.trim() });
    }
    if (website) {
      filters.push({ field: 'website', operator: 'contains', value: website.trim() });
    }

    return filters;
  }, [name, address, state, website]);

  const updateFieldPredicates = useCallback(
    (schemaId: number, fieldKey: string, next: FormSearchPredicateData[]) => {
      setFormPredicatesBySchema((prev) => {
        const others = (prev[schemaId] ?? []).filter((p) => p.fieldKey !== fieldKey);
        const merged = [...others, ...next];
        const updated = { ...prev };
        if (merged.length === 0) {
          delete updated[schemaId];
        } else {
          updated[schemaId] = merged;
        }
        return updated;
      });
    },
    [],
  );

  const handleSchemaToggle = useCallback(
    (schemaId: number, open: boolean) => {
      setCollapsedSchemaIds((prev) => {
        const next = new Set(prev);
        if (open) next.delete(schemaId);
        else next.add(schemaId);
        return next;
      });
    },
    [],
  );

  const doSearch = useCallback(() => {
    // First schema with non-empty predicates wins (MVP "either/or" — see header note).
    const activeSchema = formSchemas.find(
      (s) => (formPredicatesBySchema[s.schemaId] ?? []).length > 0,
    );
    if (activeSchema) {
      setIsFormMode(true);
      setIsFormSearching(true);
      setHasFormSearched(true);
      api.searchFormInstances(
        {
          schemaId: activeSchema.schemaId,
          attachTo: 'CLIENT',
          predicates: formPredicatesBySchema[activeSchema.schemaId] ?? [],
          page: 0,
          pageSize: 50,
        },
        (page: FormSearchResultPageData) => {
          const rows = (page.results ?? [])
            .filter((r) => r.host)
            .map((r) => ({
              id: r.host!.recordId,
              name: r.host!.displayName,
              locFormattedAddress: r.host!.displayAddress,
              _matchedSchema: activeSchema.schemaName,
              _matchedSummary: r.summaryFields,
            }));
          setFormResults(rows);
          setFormTotalRecords(page.total ?? rows.length);
          setIsFormSearching(false);
        },
        () => {
          setFormResults([]);
          setFormTotalRecords(0);
          setIsFormSearching(false);
        },
      );
    } else {
      setIsFormMode(false);
      performSearch(buildFilters());
    }
  }, [formSchemas, formPredicatesBySchema, performSearch, buildFilters]);

  const clearSearch = useCallback(() => {
    setName('');
    setAddress('');
    setState('');
    setWebsite('');
    setFormPredicatesBySchema({});
    setFormResults([]);
    setFormTotalRecords(0);
    setHasFormSearched(false);
    setIsFormMode(false);
    clearResults();
  }, [clearResults]);

  const handleView = useCallback((item: any) => {
    onView?.(item);
  }, [onView]);

  const handleAdd = useCallback((item: any) => {
    onAdd?.(item);
  }, [onAdd]);

  const handleNew = useCallback(() => {
    onNew?.();
  }, [onNew]);

  const handleRowClick = useCallback((item: any) => {
    if (searchMode === 'default') {
      handleView(item);
    } else if (showAddButton) {
      handleAdd(item);
    }
  }, [searchMode, showAddButton, handleView, handleAdd]);

  const onPageChange = useCallback((page: number) => {
    // MVP: form-mode pagination is not implemented (always page 0, size 50).
    if (isFormMode) return;
    handlePageChange(page, buildFilters());
  }, [isFormMode, handlePageChange, buildFilters]);

  const onPageSizeChange = useCallback((size: number) => {
    if (isFormMode) return;
    handlePageSizeChange(size, buildFilters());
  }, [isFormMode, handlePageSizeChange, buildFilters]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      doSearch();
    }
  }, [doSearch]);

  const renderCardTitle = useCallback((item: any) => item.name || 'Unknown Client', []);

  // Dispatch a single field to the appropriate Phase F filter component.
  // The lean `FormSearchableFieldData` is structurally a subset of `FormFieldData`
  // (key, label, type, optional options) so the filter components — which only
  // read those four properties — accept it without conversion.
  const renderFieldFilter = (schemaId: number, field: FormSearchableFieldData) => {
    const fieldPredicates = (formPredicatesBySchema[schemaId] ?? []).filter(
      (p) => p.fieldKey === field.key,
    );
    const onPredicatesChange = (next: FormSearchPredicateData[]) =>
      updateFieldPredicates(schemaId, field.key, next);
    const props = {
      field: field as unknown as FormFieldData,
      predicates: fieldPredicates,
      onPredicatesChange,
    };
    switch (field.type) {
      case 'text':
      case 'textarea':
      case 'phone':
      case 'email':
        return <TextFilter key={field.key} {...props} />;
      case 'date':
        return <DateFilter key={field.key} {...props} />;
      case 'number':
      case 'computed':
        return <NumberFilter key={field.key} {...props} />;
      case 'radio':
        return <RadioFilter key={field.key} {...props} />;
      case 'select':
        return <SelectFilter key={field.key} {...props} />;
      default:
        return null;
    }
  };

  const displayResults = isFormMode ? formResults : results;
  const displayTotal = isFormMode ? formTotalRecords : totalRecords;
  const displayLoading = isFormMode ? isFormSearching : isLoading;
  const displayHasSearched = isFormMode ? hasFormSearched : hasSearched;

  return (
    <div className={`npsd-search-client ${theme}`}>
      <NPSDSearchFilters
        activeFilterCount={activeFilterCount}
        loading={displayLoading}
        onSearch={doSearch}
      >
        <div className="search-fields">
          <div className="field-row">
            <div className="field">
              <label>Client Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Client Name"
              />
            </div>
            <div className="field">
              <label>Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Address"
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>State</label>
              <Dropdownlist
                value={state}
                items={stateItems}
                labelKey="label"
                valueKey="value"
                placeholder="Select State"
                clearable={true}
                onChange={(val) => setState(val as string | null)}
              />
            </div>
            <div className="field">
              <label>Website</label>
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Website"
              />
            </div>
          </div>

          {formSchemas.map((schema) => (
            // The endpoint already filters out schemas with zero searchable fields,
            // so every entry here renders. The `<details>` defaults to expanded so
            // the filter inputs are visible without a discovery step — admins who
            // came here looking for a field they just marked searchable shouldn't
            // have to find a closed disclosure first.
            <details
              key={schema.schemaId}
              className="form-filter-section"
              open={!collapsedSchemaIds.has(schema.schemaId)}
              onToggle={(e) =>
                handleSchemaToggle(schema.schemaId, (e.currentTarget as HTMLDetailsElement).open)
              }
            >
              <summary>{schema.schemaName} filters</summary>
              <div className="form-filter-body">
                {schema.fields.map((field) => renderFieldFilter(schema.schemaId, field))}
              </div>
            </details>
          ))}
        </div>
        <div className="search-actions">
          <button type="button" className="btn btn-primary" onClick={doSearch} disabled={displayLoading}>
            <i className="fa fa-search"></i> Search
          </button>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            <i className="fa fa-trash"></i> Clear
          </button>
          {canShowNewButton && (
            <button type="button" className="btn btn-success" onClick={handleNew}>
              <i className="fa fa-plus"></i> New
            </button>
          )}
        </div>
      </NPSDSearchFilters>

      {/* Results */}
      <NPSDSearchResults
        columns={columns}
        results={displayResults}
        loading={displayLoading}
        hasSearched={displayHasSearched}
        totalRecords={displayTotal}
        currentPage={currentPage}
        pageSize={pageSize}
        renderCardTitle={renderCardTitle}
        onRowClick={handleRowClick}
        onLinkClick={handleView}
        onCurrentPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

(window as any).NPSDSearchClientReact = NPSDSearchClient;
